/*
 * wall-set-gallery-renderer.js
 *
 * Live compositor for Wall Set product page gallery slots 2-4.
 *
 * Reads `recipe.product_scenes` (embedded by the publisher) and listens
 * for `wallset:selection:changed` from wall-set-picker.js. For each slot
 * 2-4 it picks the scene authored for the customer's current
 * (slot_index, size_group) tuple, scales the whole arrangement around
 * its center for non-reference sizes within the group, and composites
 * each print's pre-rendered hero onto the scene background canvas.
 *
 * Scaling rules (matches admin authoring):
 *   - Each scene's anchor rects are authored at 2:3 width = reference
 *     width at the LARGEST size of the size group.
 *   - scale = customer_size.w / reference.w  (uniform; preserves spacing)
 *   - For each anchor:
 *       center = (anchor.x + anchor.w/2, anchor.y + anchor.w*1.5/2)
 *       scaled_center = arrangement_center
 *                       + (center - arrangement_center) * scale
 *       print_w_px = anchor.w * scale * (customer_size.w / reference.w)
 *                  = anchor.w * scale²  ... wait that's wrong.
 *     Actually: anchor.w IS the reference print's pixel width at scale=1.
 *     For a smaller size we want spacing AND print_w to scale uniformly,
 *     so print_w_px = anchor.w * scale.
 *   - print_h_px = print_w_px * (customer_size.h / customer_size.w)
 *     (aspect-ratio aware — 4:5 renders taller than the 2:3 anchor, etc.)
 *
 * Slots without a matching scene fall back to slot 1's pre-rendered hero
 * (same as the active slide), so the gallery never has empty slides.
 */
(function () {
  'use strict';
  console.info('[wall-set-gallery] script loaded');

  const SIZE_GROUPS = {
    small:  { sizes: ['8x10', '12x18'],                    referenceWidthInches: 12 },
    medium: { sizes: ['16x20', '16x24', '18x24', '20x30', '24x36'], referenceWidthInches: 24 },
    large:  { sizes: ['32x48', '40x60'],                   referenceWidthInches: 40 },
  };

  function sizeGroupForSize(size) {
    if (!size) return 'medium';
    const lc = String(size).toLowerCase().trim();
    for (const [group, def] of Object.entries(SIZE_GROUPS)) {
      if (def.sizes.includes(lc)) return group;
    }
    // Fallback: classify by dimension (matches single-product's renderer).
    const m = lc.match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)$/);
    if (!m) return 'medium';
    const w = parseFloat(m[1]);
    const h = parseFloat(m[2]);
    if (!Number.isFinite(w) || !Number.isFinite(h)) return 'medium';
    const longestEdge = Math.max(w, h);
    const shortestEdge = Math.min(w, h);
    if (longestEdge >= 48 || shortestEdge >= 32) return 'large';
    if (shortestEdge <= 12) return 'small';
    return 'medium';
  }

  function parseSize(size) {
    if (!size) return null;
    const m = String(size).toLowerCase().trim().match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)$/);
    if (!m) return null;
    const w = parseFloat(m[1]);
    const h = parseFloat(m[2]);
    if (!Number.isFinite(w) || !Number.isFinite(h)) return null;
    return { w, h };
  }

  const SLOT_SELECTOR = '[data-wall-art-dynamic-slot]';
  const FRAME_SLICE_PX = 120;     // fallback when alpha-detect fails
  const imageCache = new Map();
  const frameSliceCache = new Map();

  /** Read the same WallArtGalleryRendererConfig the single-product
   *  renderer uses — provides frame asset URLs + size-grouped frame
   *  thicknesses. Shared config means merchant updates in one place. */
  function readSharedConfig() {
    const tag = document.querySelector('script[id^="WallArtGalleryRendererConfig-"]');
    if (!tag || !tag.textContent) return null;
    try { return JSON.parse(tag.textContent); } catch { return null; }
  }
  const sharedConfig = readSharedConfig();

  function isFramedFormat(format) {
    return format === 'framed_art_print' || format === 'framed_canvas_print';
  }

  /** Map our 'natural_oak' frame_color to the asset map's 'natural' key.
   *  Other colors pass through unchanged. */
  function frameAssetKey(frameColor) {
    if (frameColor === 'natural_oak') return 'natural';
    return frameColor;
  }

  function getFrameOverlayUrl(format, frameColor) {
    if (!sharedConfig || !sharedConfig.frames) return '';
    const byProduct = sharedConfig.frames[format];
    if (!byProduct) return '';
    return byProduct[frameAssetKey(frameColor)] || '';
  }

  function getFrameThicknessPx(format, sizeGroup) {
    if (!sharedConfig) return 0;
    const grouped = sharedConfig.frameThicknessByProductTypeBySizeGroup;
    if (grouped && grouped[format] && grouped[format][sizeGroup] != null) {
      const v = parseFloat(grouped[format][sizeGroup]);
      if (Number.isFinite(v) && v > 0) return Math.round(v);
    }
    const flat = sharedConfig.frameThicknessByProductType;
    if (flat && flat[format] != null) {
      const v = parseFloat(flat[format]);
      if (Number.isFinite(v) && v > 0) return Math.round(v);
    }
    return 48;
  }

  /** Port of single-product's drawNineSliceImage. */
  function drawNineSliceImage(ctx, image, src, dest) {
    let srcLeft = Math.max(0, Math.min(src.sliceLeft || 0, src.w));
    let srcRight = Math.max(0, Math.min(src.sliceRight || 0, src.w));
    let srcTop = Math.max(0, Math.min(src.sliceTop || 0, src.h));
    let srcBottom = Math.max(0, Math.min(src.sliceBottom || 0, src.h));
    if (srcLeft + srcRight > src.w) {
      const s = src.w / (srcLeft + srcRight);
      srcLeft *= s; srcRight *= s;
    }
    if (srcTop + srcBottom > src.h) {
      const s = src.h / (srcTop + srcBottom);
      srcTop *= s; srcBottom *= s;
    }
    let destLeft = Math.max(0, Math.min(dest.sliceLeft || 0, dest.w));
    let destRight = Math.max(0, Math.min(dest.sliceRight || 0, dest.w));
    let destTop = Math.max(0, Math.min(dest.sliceTop || 0, dest.h));
    let destBottom = Math.max(0, Math.min(dest.sliceBottom || 0, dest.h));
    if (destLeft + destRight > dest.w) {
      const s = dest.w / (destLeft + destRight);
      destLeft *= s; destRight *= s;
    }
    if (destTop + destBottom > dest.h) {
      const s = dest.h / (destTop + destBottom);
      destTop *= s; destBottom *= s;
    }
    const sx = [src.x, src.x + srcLeft, src.x + src.w - srcRight, src.x + src.w];
    const sy = [src.y, src.y + srcTop, src.y + src.h - srcBottom, src.y + src.h];
    const dx = [dest.x, dest.x + destLeft, dest.x + dest.w - destRight, dest.x + dest.w];
    const dy = [dest.y, dest.y + destTop, dest.y + dest.h - destBottom, dest.y + dest.h];
    function drawPatch(sx0, sy0, sx1, sy1, dx0, dy0, dx1, dy1) {
      const sw = sx1 - sx0, sh = sy1 - sy0;
      const dw = dx1 - dx0, dh = dy1 - dy0;
      if (sw <= 0 || sh <= 0 || dw <= 0 || dh <= 0) return;
      ctx.drawImage(image, sx0, sy0, sw, sh, dx0, dy0, dw, dh);
    }
    for (let row = 0; row < 3; row += 1) {
      for (let col = 0; col < 3; col += 1) {
        drawPatch(sx[col], sy[row], sx[col + 1], sy[row + 1],
                  dx[col], dy[row], dx[col + 1], dy[row + 1]);
      }
    }
  }

  /** Auto-detect the slice size from the frame image's transparent
   *  aperture (alpha = 0 in the middle). Same algorithm as the
   *  single-product renderer; cached per image src. */
  function getFrameSourceSlices(image) {
    const cacheKey = image?.currentSrc || image?.src || '';
    if (frameSliceCache.has(cacheKey)) return frameSliceCache.get(cacheKey);
    const width = image.naturalWidth || image.width || 0;
    const height = image.naturalHeight || image.height || 0;
    if (width <= 0 || height <= 0) {
      const fallback = { top: FRAME_SLICE_PX, right: FRAME_SLICE_PX, bottom: FRAME_SLICE_PX, left: FRAME_SLICE_PX };
      frameSliceCache.set(cacheKey, fallback);
      return fallback;
    }
    const off = document.createElement('canvas');
    off.width = width; off.height = height;
    const octx = off.getContext('2d', { willReadFrequently: true });
    if (!octx) {
      const fb = { top: FRAME_SLICE_PX, right: FRAME_SLICE_PX, bottom: FRAME_SLICE_PX, left: FRAME_SLICE_PX };
      frameSliceCache.set(cacheKey, fb);
      return fb;
    }
    octx.clearRect(0, 0, width, height);
    octx.drawImage(image, 0, 0, width, height);
    const data = octx.getImageData(0, 0, width, height).data;
    const alphaThreshold = 8;
    const midX = Math.floor(width / 2), midY = Math.floor(height / 2);
    const alphaAt = (x, y) => data[(y * width + x) * 4 + 3];
    let leftEdge = -1; for (let x = 0; x < width; x++) { if (alphaAt(x, midY) <= alphaThreshold) { leftEdge = x; break; } }
    let rightEdge = -1; for (let x = width - 1; x >= 0; x--) { if (alphaAt(x, midY) <= alphaThreshold) { rightEdge = x; break; } }
    let topEdge = -1; for (let y = 0; y < height; y++) { if (alphaAt(midX, y) <= alphaThreshold) { topEdge = y; break; } }
    let bottomEdge = -1; for (let y = height - 1; y >= 0; y--) { if (alphaAt(midX, y) <= alphaThreshold) { bottomEdge = y; break; } }
    const ok = leftEdge >= 0 && rightEdge >= 0 && topEdge >= 0 && bottomEdge >= 0 && leftEdge < rightEdge && topEdge < bottomEdge;
    if (!ok) {
      const fb = { top: FRAME_SLICE_PX, right: FRAME_SLICE_PX, bottom: FRAME_SLICE_PX, left: FRAME_SLICE_PX };
      frameSliceCache.set(cacheKey, fb);
      return fb;
    }
    const detected = {
      top: Math.max(1, Math.min(topEdge, height - 1)),
      right: Math.max(1, Math.min(width - 1 - rightEdge, width - 1)),
      bottom: Math.max(1, Math.min(height - 1 - bottomEdge, height - 1)),
      left: Math.max(1, Math.min(leftEdge, width - 1)),
    };
    frameSliceCache.set(cacheKey, detected);
    return detected;
  }

  function loadImage(url) {
    if (!url) return Promise.resolve(null);
    if (imageCache.has(url)) return imageCache.get(url);
    const p = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = (err) => {
        console.warn('[wall-set-gallery] image load failed', url, err);
        resolve(null);
      };
      img.src = url;
    });
    imageCache.set(url, p);
    return p;
  }

  /** Find or insert a canvas into a slot's existing image container. */
  function ensureCanvas(slotEl) {
    let canvas = slotEl.querySelector('canvas[data-wall-set-canvas]');
    if (canvas) return canvas;
    canvas = document.createElement('canvas');
    canvas.setAttribute('data-wall-set-canvas', '');
    canvas.style.cssText = 'width:100%;height:auto;display:block;';
    // Wipe whatever placeholder media is in the slot — usually a
    // <product-thumbnail> SVG placeholder. Keep the wrapper.
    const wrap = slotEl.querySelector('.product-media-container') || slotEl;
    wrap.innerHTML = '';
    wrap.style.background = 'transparent';
    wrap.appendChild(canvas);
    return canvas;
  }

  async function renderSlot(slotEl, scene, lines, customerSize, productType, frameColor, sizeGroup) {
    if (!scene || !customerSize) return;
    const bgImg = await loadImage(scene.bg_url);
    if (!bgImg) return;
    const canvas = ensureCanvas(slotEl);
    canvas.width = scene.bg_w;
    canvas.height = scene.bg_h;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bgImg, 0, 0, scene.bg_w, scene.bg_h);

    // Group-scale factor. The anchor's `w` was authored at the LARGEST
    // size of the size group as a 2:3 reference; for any other size we
    // scale uniformly so spacing tracks size.
    const groupDef = SIZE_GROUPS[scene.size_group];
    const referenceW = groupDef ? groupDef.referenceWidthInches : customerSize.w;
    const scale = customerSize.w / referenceW;

    // Arrangement center in pixel space (average of anchor centers — the
    // pivot we scale around so spacing shrinks/expands symmetrically).
    let cxSum = 0, cySum = 0;
    for (const a of scene.anchors) {
      cxSum += a.x + a.w / 2;
      cySum += a.y + (a.w * 1.5) / 2;
    }
    const arrangementCenterX = cxSum / scene.anchors.length;
    const arrangementCenterY = cySum / scene.anchors.length;

    // Aspect ratio of the customer's chosen print (height / width). 4:5
    // renders taller than the 2:3 anchor; 3:4 a bit taller; 2:3 matches.
    const printAspectHW = customerSize.h / customerSize.w;

    // Pre-load the frame overlay once per slot (same color for all
    // prints in the set since format is uniform).
    const frameOverlayUrl = isFramedFormat(productType) && frameColor && frameColor !== 'none'
      ? getFrameOverlayUrl(productType, frameColor)
      : '';
    const frameImg = frameOverlayUrl ? await loadImage(frameOverlayUrl) : null;
    // Frame thickness comes straight from the theme editor's
    // per-size-group setting (matches single-product behavior). NOT
    // multiplied by `scale` — the merchant authors a different
    // thickness per size group (small/medium/large) precisely so the
    // visible frame can stay consistent across the group's sizes.
    const frameThickness = isFramedFormat(productType)
      ? getFrameThicknessPx(productType, sizeGroup)
      : 0;
    console.info('[wall-set-gallery] frame resolution', {
      productType, frameColor, sizeGroup,
      isFramed: isFramedFormat(productType),
      assetKey: frameAssetKey(frameColor),
      frameOverlayUrl,
      frame_image_loaded: !!frameImg,
      frameThickness_px: frameThickness,
    });
    // Dump the raw frames structure as a string so we can see exactly
    // what keys/values exist without relying on console object expansion.
    console.info('[wall-set-gallery] raw frames JSON:',
      JSON.stringify(sharedConfig && sharedConfig.frames, null, 2));

    for (const anchor of scene.anchors) {
      const line = lines.find((l) => l.plane_number === anchor.plane_number);
      const artworkUrl = (line && (line.artwork_url || line.design_image)) || null;
      if (!line || !artworkUrl) continue;
      const printImg = await loadImage(artworkUrl);
      if (!printImg) continue;

      const anchorCenterX = anchor.x + anchor.w / 2;
      const anchorCenterY = anchor.y + (anchor.w * 1.5) / 2;
      const relCenterX = anchorCenterX - arrangementCenterX;
      const relCenterY = anchorCenterY - arrangementCenterY;
      const scaledCenterX = arrangementCenterX + relCenterX * scale;
      const scaledCenterY = arrangementCenterY + relCenterY * scale;

      // The PRINT's overall pixel rect (artwork + frame combined). When
      // framed, we inset by frameThickness to get the artwork rect and
      // 9-slice the frame around it.
      const printWpx = anchor.w * scale;
      const printHpx = printWpx * printAspectHW;
      const drawX = scaledCenterX - printWpx / 2;
      const drawY = scaledCenterY - printHpx / 2;

      const outerRect = { x: drawX, y: drawY, w: printWpx, h: printHpx };
      const artworkRect = frameImg && frameThickness > 0
        ? {
            x: drawX + frameThickness,
            y: drawY + frameThickness,
            w: Math.max(1, printWpx - frameThickness * 2),
            h: Math.max(1, printHpx - frameThickness * 2),
          }
        : outerRect;

      // Shadow uses the OUTER rect (frame is the visible edge that casts).
      drawProductShadow(ctx, outerRect, productType);
      // Artwork sits inside the frame (or fills the rect when unframed).
      ctx.drawImage(printImg, artworkRect.x, artworkRect.y, artworkRect.w, artworkRect.h);

      // Frame overlay — 9-slice scaling so the corners stay sharp.
      if (frameImg && frameThickness > 0) {
        const sw = frameImg.naturalWidth || frameImg.width;
        const sh = frameImg.naturalHeight || frameImg.height;
        const srcSlices = getFrameSourceSlices(frameImg);
        drawNineSliceImage(ctx, frameImg,
          { x: 0, y: 0, w: sw, h: sh,
            sliceTop: srcSlices.top, sliceRight: srcSlices.right,
            sliceBottom: srcSlices.bottom, sliceLeft: srcSlices.left },
          { x: outerRect.x, y: outerRect.y, w: outerRect.w, h: outerRect.h,
            sliceTop: frameThickness, sliceRight: frameThickness,
            sliceBottom: frameThickness, sliceLeft: frameThickness },
        );
      }
    }

    // Mirror the rendered composite into the slot's thumbnail (Dawn's
    // thumbnail list shows separate <img>s, not the slide itself).
    syncThumbnail(slotEl, canvas);
  }

  /** Locate the thumbnail <img> for this slot and replace its src with
   *  the rendered canvas as a data URL. Keeps the thumbnail in sync
   *  with the main slide on every variant change. */
  function syncThumbnail(slotEl, canvas) {
    const slotIndex = slotEl.getAttribute('data-wall-art-dynamic-slot');
    if (!slotIndex) return;
    const thumbLi = document.querySelector(
      `[data-wall-art-dynamic-thumb-slot="${slotIndex}"]`,
    );
    if (!thumbLi) return;
    const thumbImg = thumbLi.querySelector('img');
    if (!thumbImg) return;
    // toDataURL is expensive on a big canvas; encode at a smaller size
    // to keep the thumbnail update under ~50ms.
    try {
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      thumbImg.setAttribute('src', dataUrl);
      if (thumbImg.hasAttribute('srcset')) thumbImg.removeAttribute('srcset');
      const picture = thumbImg.closest('picture');
      if (picture) {
        for (const s of picture.querySelectorAll('source')) s.remove();
      }
    } catch (err) {
      console.warn('[wall-set-gallery] thumbnail sync failed', err);
    }
  }

  /** Shadow profile + draw — mirrors single-product wall-art-gallery-renderer.js
   *  defaults exactly (distance/blur/cast trail/contact at 100% scale,
   *  directionX=0.8, directionY=1.15). The user can tune these via the
   *  same theme settings as single-product later; for now defaults match. */
  const SHADOW_DEFAULTS = {
    distanceScale: 1.0,
    directionX: 0.8,
    directionY: 1.15,
    blurScale: 1.0,
    castTrailScale: 1.0,
    directionalStrengthScale: 1.0,
    contactStrengthScale: 1.0,
    opacityScale: 1.0,
  };

  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

  function drawProductShadow(ctx, rect, productType) {
    const isCanvasType = productType === 'canvas_print' || productType === 'framed_canvas_print';
    const isFramedType = productType === 'framed_art_print' || productType === 'framed_canvas_print';
    const p = SHADOW_DEFAULTS;
    const maxEdge = Math.max(rect.w, rect.h);
    const scaledDistance = Math.max(0, maxEdge * 0.018 * p.distanceScale);
    const baseDistance = Math.max(0, Math.min(24 * p.distanceScale, scaledDistance));
    if (p.opacityScale <= 0 || baseDistance <= 0) return;

    // Ambient pass — seats the print in the scene.
    ctx.save();
    const ambientOpacity = clamp((isFramedType ? 0.12 : 0.08) * p.opacityScale, 0, 1);
    ctx.shadowColor = `rgba(0, 0, 0, ${ambientOpacity})`;
    ctx.shadowBlur = (isFramedType ? 18 : 14) * p.blurScale;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = Math.max(0, baseDistance * 0.2);
    ctx.fillStyle = 'rgba(0, 0, 0, 1)';
    ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
    ctx.restore();

    // Directional cast shadow trail — multiple passes stretched along
    // the light direction for a soft elongated drop shadow.
    const castOpacity = clamp(
      (isCanvasType ? 0.26 : 0.22) * p.opacityScale * p.directionalStrengthScale, 0, 1,
    );
    const castBlurBase = (isCanvasType ? 22 : 18) * p.blurScale;
    const castTrailScale = p.castTrailScale;
    const castPassCount = Math.round(clamp(4 + p.blurScale * 4 * Math.max(0.4, castTrailScale), 4, 18));
    const directionalSpread = 1.15 * castTrailScale;
    for (let i = 0; i < castPassCount; i += 1) {
      const t = (i + 1) / castPassCount;
      const passOpacity = clamp((castOpacity * (1 - t * 0.65) * 1.9) / castPassCount, 0, 1);
      if (passOpacity <= 0) continue;
      ctx.save();
      ctx.shadowColor = `rgba(0, 0, 0, ${passOpacity})`;
      ctx.shadowBlur = castBlurBase * (0.4 + t * 0.8);
      ctx.shadowOffsetX = baseDistance * p.directionX * (0.25 + t * directionalSpread);
      ctx.shadowOffsetY = baseDistance * p.directionY * (0.25 + t * directionalSpread);
      ctx.fillStyle = 'rgba(0, 0, 0, 1)';
      ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
      ctx.restore();
    }

    // Contact pass — tight shadow near the right/bottom edges so the
    // print looks like it's actually touching the wall.
    ctx.save();
    const contactOpacity = clamp(0.18 * p.opacityScale * p.contactStrengthScale, 0, 1);
    ctx.shadowColor = `rgba(0, 0, 0, ${contactOpacity})`;
    ctx.shadowBlur = (isFramedType ? 10 : 8) * p.blurScale;
    ctx.shadowOffsetX = baseDistance * p.directionX * 0.56;
    ctx.shadowOffsetY = baseDistance * p.directionY * 0.56;
    ctx.fillStyle = 'rgba(0, 0, 0, 1)';
    ctx.fillRect(rect.x + 2, rect.y + 2, rect.w - 4, rect.h - 4);
    ctx.restore();
  }

  let latestState = null;
  let recipe = null;

  async function rerenderAll() {
    if (!recipe || !latestState) {
      console.info('[wall-set-gallery] rerenderAll skipped — recipe or state missing', { recipe: !!recipe, latestState: !!latestState });
      return;
    }
    if (!recipe.product_scenes || recipe.product_scenes.length === 0) {
      console.info('[wall-set-gallery] rerenderAll skipped — no product_scenes in recipe', {
        product_scenes_present: 'product_scenes' in recipe,
        product_scenes_length: (recipe.product_scenes || []).length,
        recipe_keys: Object.keys(recipe || {}),
      });
      return;
    }
    const sizeStr = latestState.size || (recipe.is_constant_size && recipe.authored_size) || null;
    const customerSize = parseSize(sizeStr);
    if (!customerSize) {
      console.warn('[wall-set-gallery] no size to render against');
      return;
    }
    const group = sizeGroupForSize(sizeStr);
    const slots = document.querySelectorAll(SLOT_SELECTOR);
    console.info('[wall-set-gallery] rerendering', {
      slot_count: slots.length,
      size: sizeStr, group,
      product_scenes_available: recipe.product_scenes.map((s) => `slot${s.slot_index}/${s.size_group}`),
    });
    for (const slotEl of slots) {
      const slotIndex = parseInt(slotEl.getAttribute('data-wall-art-dynamic-slot'), 10);
      if (![2, 3, 4].includes(slotIndex)) continue;
      // Find a scene matching (slot, group) — fall back to medium if the
      // exact group isn't authored, then any size_group for this slot.
      const scene =
        recipe.product_scenes.find((s) => s.slot_index === slotIndex && s.size_group === group)
        || recipe.product_scenes.find((s) => s.slot_index === slotIndex && s.size_group === 'medium')
        || recipe.product_scenes.find((s) => s.slot_index === slotIndex);
      if (!scene) {
        console.info(`[wall-set-gallery] no scene for slot ${slotIndex} / ${group} — leaving slot as-is`);
        continue;
      }
      try {
        const firstLine = latestState.lines && latestState.lines[0];
        const productType = firstLine ? firstLine.format : 'art_print';
        const frameColor = firstLine ? firstLine.frame_color : 'none';
        console.info(`[wall-set-gallery] rendering slot ${slotIndex} with scene`, {
          slot_index: scene.slot_index, size_group: scene.size_group,
          bg_url: scene.bg_url, anchors: scene.anchors.length,
          productType, frameColor,
        });
        await renderSlot(slotEl, scene, latestState.lines, customerSize, productType, frameColor, group);
      } catch (err) {
        console.error(`[wall-set-gallery] slot ${slotIndex} render failed`, err);
      }
    }
  }

  /** Find the recipe via the wall-set-picker's embedded JSON. The picker
   *  already parses + double-handles the metafield-string case, so the
   *  cleanest source is its `wallset:selection:changed` event detail. */
  console.info('[wall-set-gallery] registering event listener');
  document.addEventListener('wallset:selection:changed', (ev) => {
    console.info('[wall-set-gallery] selection:changed received');
    const detail = ev && ev.detail;
    if (!detail) return;
    latestState = { lines: detail.lines, size: detail.state && detail.state.size };
    recipe = detail.recipe;
    if (!recipe.__loggedBoot) {
      console.info('[wall-set-gallery] booted; recipe keys =', Object.keys(recipe || {}), {
        product_scenes_count: (recipe.product_scenes || []).length,
        slot_dom_count: document.querySelectorAll(SLOT_SELECTOR).length,
      });
      recipe.__loggedBoot = true;
    }
    requestAnimationFrame(() => { void rerenderAll(); });
  });
})();
